import { AvailityElement } from "risa-data-model";
import {
  AddAnotherDiagnosisAddButton,
  AddAnotherProcedureAddButton,
  AddAnotherProviderAddButton,
  AvailityProcedureElements,
  DiagnosisElements,
  KeyLabelElementObject,
  RenderingProviderElements,
} from "./procedurePaFromConstants";
// TODO break file into smaller files.
// TODO break function into smaller elements.
export function filterRequiredAllowedElements(
  elements:
    | { [key: string]: AvailityElement }
    | { [key: string]: AvailityElement }[]
    | undefined,
): { [key: string]: AvailityElement } | { [key: string]: AvailityElement }[] {
  function traverse(
    elements:
      | { [key: string]: AvailityElement }
      | { [key: string]: AvailityElement }[]
      | undefined,
  ): { [key: string]: AvailityElement } | { [key: string]: AvailityElement }[] {
    if (Array.isArray(elements)) {
      // Traverse array and flatten results, ensuring no nested arrays
      return elements
        .map((group) => traverse(group))
        .filter((result) => Object.keys(result).length > 0) as {
        [key: string]: AvailityElement;
      }[];
    } else {
      const filteredElements: { [key: string]: AvailityElement } = {};

      for (const key in elements) {
        if (elements.hasOwnProperty(key)) {
          const element = elements[key];

          if (element.allowed) {
            const nestedFilteredElements = element.elements
              ? traverse(element.elements)
              : undefined;

            filteredElements[key] = {
              ...element,
              elements:
                Array.isArray(nestedFilteredElements) &&
                nestedFilteredElements.length > 0
                  ? nestedFilteredElements
                  : !Array.isArray(nestedFilteredElements) &&
                      nestedFilteredElements &&
                      Object.keys(nestedFilteredElements).length > 0
                    ? nestedFilteredElements
                    : undefined,
            };
          }
        }
      }

      return filteredElements;
    }
  }

  return traverse(elements);
}

// TODO break function into smaller elements.
export function populateKeyLabelElements(
  filteredElements:
    | { [key: string]: AvailityElement }
    | { [key: string]: AvailityElement }[],
  keyLabelElementObjects: KeyLabelElementObject[],
): KeyLabelElementObject[] {
  const expandedElements: KeyLabelElementObject[] = [];
  const arrayKeys = new Set(["diagnoses", "procedures", "renderingProviders"]); // Define keys that represent array-like structures

  keyLabelElementObjects.forEach((item) => {
    const { key, label } = item;

    // Case 1: Special keys (Heading, Checkbox, AddButton)
    if (key.includes("Heading")) {
      expandedElements.push({
        ...item,
        element: { label, type: "Heading", key },
      });
      return;
    }

    if (key.includes("Checkbox")) {
      expandedElements.push({
        ...item,
        element: { label, type: "Checkbox", key },
      });
      return;
    }

    if (key.includes("AddButton")) {
      expandedElements.push({
        ...item,
        element: { label, type: "AddButton", key },
      });
      return;
    }

    // Case 2: Simple keys like requestTypeCode
    if (filteredElements[key] && !filteredElements[key].elements) {
      expandedElements.push({
        ...item,
        element: filteredElements[key] as AvailityElement,
      });
      return;
    }

    // Case 3: Nested keys like subscriber.firstName
    // Exclude array-like keys from this case
    if (key.includes(".") && !arrayKeys.has(key.split(".")[0])) {
      const keys = key.split(".");
      let nestedElement: { [key: string]: AvailityElement } =
        filteredElements[keys[0]];

      if (nestedElement) {
        expandedElements.push({
          ...item,
          element: nestedElement.elements[keys[1]] as AvailityElement,
        });
      }
      return;
    }

    // Case 4: Array-like keys (diagnoses, procedures, renderingProviders, etc.)
    if (arrayKeys.has(key) && Array.isArray(filteredElements[key]?.elements)) {
      const arrayItems = filteredElements[key].elements as {
        [key: string]: AvailityElement;
      }[];

      arrayItems.forEach((element, index) => {
        // Add heading for each array group
        if (index === 0) {
          expandedElements.push({
            key: `${key}Heading`,
            label: `${label}`,
            element: { label: `${label}`, type: "Heading" },
          });
        } else {
          expandedElements.push({
            key: `${key}Heading_${index}`,
            label: `${label} ${index + 1}`,
            element: { label: `${label} ${index + 1}`, type: "Heading" },
          });
        }

        // Ensure the order of fields in the array object
        const sortedFields = Object.entries(element).sort(
          ([keyA, elementA], [keyB, elementB]) => {
            const orderA = elementA.order || 0;
            const orderB = elementB.order || 0;
            return orderA - orderB;
          },
        );

        // Add individual fields within the array object in sorted order
        sortedFields.forEach(([fieldKey, fieldElement]) => {
          expandedElements.push({
            key: `${key}.${fieldKey}_${index}`,
            label: fieldElement.label || `${fieldKey}`,
            element: fieldElement,
          });
        });
      });
      expandedElements.push({
        key: `AddAnother${key.charAt(0).toUpperCase() + key.slice(1)}AddButton`,
        label: `Add Another ${label}`,
        element: { label: `Add Another ${label}`, type: "AddButton" },
      });
      return;
    }
  });

  return expandedElements;
}

// TODO break function into smaller elements.
export const modifyDataWithAddOperation = (
  keyLabelElementObject: KeyLabelElementObject[],
  key: string,
  indexOfArray: number,
): KeyLabelElementObject[] => {
  if (key.includes(AddAnotherProviderAddButton)) {
    const index = keyLabelElementObject.findIndex((item) => item.key === key);
    if (index !== -1) {
      const elementsToAdd = RenderingProviderElements(
        indexOfArray,
        indexOfArray + 1,
      ).map((item) => {
        if (!item.element) {
          const baseKey = item.key.split("_")[0];
          const matchingElement = keyLabelElementObject.find((obj) =>
            obj.key.includes(baseKey),
          );

          if (matchingElement && matchingElement.element) {
            const newElement = { ...matchingElement.element };
            // Deleting the filledValue
            delete newElement.filledValue;
            return { ...item, element: newElement };
          }
        }

        return item;
      });
      keyLabelElementObject.splice(index, 0, ...elementsToAdd);
    }
  } else if (key.includes(AddAnotherDiagnosisAddButton)) {
    const index = keyLabelElementObject.findIndex((item) => item.key === key);
    if (index !== -1) {
      const elementsToAdd = DiagnosisElements(
        indexOfArray,
        indexOfArray + 1,
      ).map((item) => {
        if (!item.element) {
          const baseKey = item.key.split("_")[0];
          const matchingElement = keyLabelElementObject.find((obj) =>
            obj.key.includes(baseKey),
          );
          if (matchingElement && matchingElement.element) {
            const newElement = { ...matchingElement.element };
            // Deleting the filledValue
            delete newElement.filledValue;
            return { ...item, element: newElement };
          }
        }

        return item;
      });
      keyLabelElementObject.splice(index, 0, ...elementsToAdd);
    }
  } else if (key.includes(AddAnotherProcedureAddButton)) {
    const index = keyLabelElementObject.findIndex((item) => item.key === key);
    if (index !== -1) {
      const elementsToAdd = AvailityProcedureElements(
        indexOfArray,
        indexOfArray + 1,
      ).map((item) => {
        if (!item.element) {
          const baseKey = item.key.split("_")[0];
          const matchingElement = keyLabelElementObject.find((obj) =>
            obj.key.includes(baseKey),
          );
          if (matchingElement && matchingElement.element) {
            const newElement = { ...matchingElement.element };
            // Deleting the filledValue
            delete newElement.filledValue;
            return { ...item, element: newElement };
          }
        }

        return item;
      });
      keyLabelElementObject.splice(index, 0, ...elementsToAdd);
    }
  }
  return keyLabelElementObject;
};

export const checkFieldValidation = (required, value, element) => {
  if (!value && required) return true;

  if (element.pattern && value && required) {
    const regex = new RegExp(element.pattern);
    if (!regex.test(value)) return true;
  }

  if (element.maxLength && value?.length > element.maxLength && required)
    return true;

  if (element.type === "Date" && required && value) {
    const dateValue = new Date(value);
    const minDate = element.min ? new Date(element.min) : null;
    const maxDate = element.max ? new Date(element.max) : null;

    if ((minDate && dateValue < minDate) || (maxDate && dateValue > maxDate))
      return true;
  }

  return false;
};
